import consola from 'consola'
import type { RouteLocationNormalized } from 'vue-router'
import { StcsEntityStatusEnum } from '~/protos/gen/stcs_enums.entity'
import { useCtxStore } from '~/stores/ctx.store'
import { urlCtxPartObj, urlSectionCtxPartObj } from '~/types/route.entity'
import { defineNuxtRouteMiddleware, navigateTo } from '#imports'

const logger = consola.withTag('[company-data-check.middleware]')

/**
 * Middleware to check if the selected company has pending data status.
 * If the company status is SES_PENDING_DATA, redirect to data-management/companies page.
 * This middleware applies to Dashboard, Financials, Analysis, and Planning routes.
 */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalized) => {
  const ctxStore = useCtxStore()
  const orgId = to?.params?.orgId as string
  const compId = to?.params?.compId as string

  // Skip if no orgId or compId in route
  // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
  if (!orgId || !compId) {
    return
  }

  // Check if the current route is one of the protected sections
  const protectedSections = [urlSectionCtxPartObj.DASHBOARD.path, urlSectionCtxPartObj.FINS.path, urlSectionCtxPartObj.ANALYSIS.path, urlSectionCtxPartObj.PLAN.path]

  const isProtectedRoute = protectedSections.some((sectionPath) => {
    const resolvedPath = sectionPath.replace(':orgId', orgId).replace(':compId', compId)
    return to.path.startsWith(resolvedPath)
  })

  if (!isProtectedRoute) {
    return
  }

  // Get company data from store
  const compData = ctxStore.ref_comp_map[compId]
  const compStatus = compData?.ref_comp?.meta?.status

  if (compStatus === StcsEntityStatusEnum.SES_PENDING_DATA) {
    const orgData = ctxStore.ref_org_map[orgId]
    const refCompMap = orgData?.meta?.ref_comp_map
    const userId = ctxStore.ref_user?.id?.extractId()

    // Data check
    const hasRequiredData = refCompMap && userId
    if (!hasRequiredData) {
      const dataManagementPath = urlCtxPartObj.DATA_COMP.path.replace(':orgId', orgId)
      logger.warn(`Cannot determine a fallback for company ${compId}. Redirecting to ${dataManagementPath}`)
      return navigateTo(dataManagementPath)
    }

    // Sorty by index, find first best company
    const sortedFallbackCandidates = Object.keys(refCompMap)
      .filter((candidateId) => {
        const candidateComp = ctxStore.ref_comp_map[candidateId]
        return candidateId !== compId && refCompMap[candidateId]?.ref_user_map[userId] && candidateComp?.ref_comp?.meta?.status === StcsEntityStatusEnum.SES_ACTIVE
      })
      .sort((a, b) => {
        const idxA = refCompMap[a]?.sort_index ?? 0
        const idxB = refCompMap[b]?.sort_index ?? 0
        return idxA - idxB || a.localeCompare(b)
      })

    const bestFallbackCompId = sortedFallbackCandidates[0]

    if (bestFallbackCompId) {
      // If valid fallback company is found, redirect to the same page but with the new company ID.
      const newPath = to.path.replace(compId, bestFallbackCompId)
      logger.warn(`Company ${compId} is pending data. Switching to best available active company ${bestFallbackCompId}. Redirecting from ${to.path} to ${newPath}`)
      return navigateTo(newPath)
    }

    // As a last resort redirect to the data management page.
    const dataManagementPath = urlCtxPartObj.DATA_COMP.path.replace(':orgId', orgId)
    logger.warn(`Company ${compId} has pending data status and no active alternatives found. Redirecting from ${to.path} to ${dataManagementPath}`)
    return navigateTo(dataManagementPath)
  }

  logger.debug(`Company ${compId} data check passed for route: ${to.path}`)
})
